// Localização: frontend/src/pages/Admin/AdminAssinaturasPage.jsx
// Página admin para gerenciar assinaturas manualmente

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import PageTitle from '../../components/Layout/PageTitle';
import LoadingIndicator from '../../components/Common/LoadingIndicator';
import Alert from '../../components/UI/Alert';
import Modal from '../../components/UI/Modal';
import PrimaryButton from '../../components/Common/PrimaryButton';
import { 
    Building2, 
    Search, 
    Filter, 
    Edit, 
    Lock, 
    Unlock, 
    RefreshCw,
    TrendingUp,
    BarChart3,
    Calendar,
    DollarSign,
    CheckCircle,
    XCircle,
    Clock,
    AlertCircle
} from 'lucide-react';
import * as adminAssinaturaApi from '../../api/adminAssinaturaApi';
import * as assinaturaApi from '../../api/assinaturaApi';

const AdminAssinaturasPage = () => {
    const { user } = useAuth();
    const navigate = useNavigate();

    // Estados
    const [loading, setLoading] = useState(true);
    const [organizacoes, setOrganizacoes] = useState([]);
    const [estatisticas, setEstatisticas] = useState(null);
    const [pagination, setPagination] = useState({ page: 1, limit: 20, total: 0, totalPages: 0 });
    const [filters, setFilters] = useState({
        status_assinatura: '',
        tipo_assinatura: '',
        status: '',
        search: ''
    });
    const [selectedOrganizacao, setSelectedOrganizacao] = useState(null);
    const [planos, setPlanos] = useState([]);
    
    // Estados de modais
    const [showDetalhesModal, setShowDetalhesModal] = useState(false);
    const [showPlanoModal, setShowPlanoModal] = useState(false);
    const [showBloquearModal, setShowBloquearModal] = useState(false);
    const [showRenovarModal, setShowRenovarModal] = useState(false);
    
    // Estados de formulários
    const [formData, setFormData] = useState({
        plano_id: '',
        observacoes: '',
        dias_adicionais: 365
    });
    const [actionLoading, setActionLoading] = useState(false);
    const [alert, setAlert] = useState({ type: '', message: '' });

    // Verificar se é admin global
    // Permite: global_admin, super_admin, admin (baseado na coluna role)
    useEffect(() => {
        if (user && !['global_admin', 'super_admin', 'admin'].includes(user.role)) {
            navigate('/dashboard');
        }
    }, [user, navigate]);

    // Carregar dados iniciais
    useEffect(() => {
        carregarDados();
        carregarPlanos();
        carregarEstatisticas();
    }, [pagination.page, filters]);

    const carregarDados = async () => {
        try {
            setLoading(true);
            const response = await adminAssinaturaApi.listarAssinaturas(
                filters,
                pagination.page,
                pagination.limit
            );
            
            if (response.success) {
                setOrganizacoes(response.data);
                setPagination(response.pagination);
            }
        } catch (error) {
            setAlert({ type: 'error', message: 'Erro ao carregar organizações' });
        } finally {
            setLoading(false);
        }
    };

    const carregarPlanos = async () => {
        try {
            const response = await assinaturaApi.listPlanos();
            if (response.success) {
                setPlanos(response.data);
            }
        } catch (error) {
            console.error('Erro ao carregar planos:', error);
        }
    };

    const carregarEstatisticas = async () => {
        try {
            const response = await adminAssinaturaApi.obterEstatisticas();
            if (response.success) {
                setEstatisticas(response.data);
            }
        } catch (error) {
            console.error('Erro ao carregar estatísticas:', error);
        }
    };

    const handleFilterChange = (key, value) => {
        setFilters(prev => ({ ...prev, [key]: value }));
        setPagination(prev => ({ ...prev, page: 1 }));
    };

    const handleAbrirDetalhes = async (organizacaoId) => {
        try {
            setLoading(true);
            const response = await adminAssinaturaApi.buscarDetalhesOrganizacao(organizacaoId);
            if (response.success) {
                setSelectedOrganizacao(response.data);
                setShowDetalhesModal(true);
            }
        } catch (error) {
            setAlert({ type: 'error', message: 'Erro ao carregar detalhes' });
        } finally {
            setLoading(false);
        }
    };

    const handleAtualizarPlano = async () => {
        if (!formData.plano_id) {
            setAlert({ type: 'error', message: 'Selecione um plano' });
            return;
        }

        try {
            setActionLoading(true);
            const response = await adminAssinaturaApi.atualizarPlano(
                selectedOrganizacao.organizacao.id,
                parseInt(formData.plano_id),
                formData.observacoes
            );

            if (response.success) {
                setAlert({ type: 'success', message: 'Plano atualizado com sucesso!' });
                setShowPlanoModal(false);
                setFormData({ plano_id: '', observacoes: '', dias_adicionais: 365 });
                carregarDados();
                carregarEstatisticas();
            }
        } catch (error) {
            setAlert({ type: 'error', message: error.response?.data?.message || 'Erro ao atualizar plano' });
        } finally {
            setActionLoading(false);
        }
    };

    const handleBloquear = async () => {
        try {
            setActionLoading(true);
            const response = await adminAssinaturaApi.bloquearOrganizacao(
                selectedOrganizacao.organizacao.id,
                formData.observacoes
            );

            if (response.success) {
                setAlert({ type: 'success', message: 'Organização bloqueada com sucesso!' });
                setShowBloquearModal(false);
                setFormData({ plano_id: '', observacoes: '', dias_adicionais: 365 });
                carregarDados();
                carregarEstatisticas();
            }
        } catch (error) {
            setAlert({ type: 'error', message: error.response?.data?.message || 'Erro ao bloquear organização' });
        } finally {
            setActionLoading(false);
        }
    };

    const handleDesbloquear = async () => {
        try {
            setActionLoading(true);
            const response = await adminAssinaturaApi.desbloquearOrganizacao(
                selectedOrganizacao.organizacao.id,
                formData.observacoes
            );

            if (response.success) {
                setAlert({ type: 'success', message: 'Organização desbloqueada com sucesso!' });
                setFormData({ plano_id: '', observacoes: '', dias_adicionais: 365 });
                carregarDados();
                carregarEstatisticas();
                setShowDetalhesModal(false);
            }
        } catch (error) {
            setAlert({ type: 'error', message: error.response?.data?.message || 'Erro ao desbloquear organização' });
        } finally {
            setActionLoading(false);
        }
    };

    const handleRenovar = async () => {
        try {
            setActionLoading(true);
            const response = await adminAssinaturaApi.renovarAssinatura(
                selectedOrganizacao.organizacao.id,
                parseInt(formData.dias_adicionais),
                formData.observacoes
            );

            if (response.success) {
                setAlert({ type: 'success', message: 'Assinatura renovada com sucesso!' });
                setShowRenovarModal(false);
                setFormData({ plano_id: '', observacoes: '', dias_adicionais: 365 });
                carregarDados();
                carregarEstatisticas();
            }
        } catch (error) {
            setAlert({ type: 'error', message: error.response?.data?.message || 'Erro ao renovar assinatura' });
        } finally {
            setActionLoading(false);
        }
    };

    const getStatusBadge = (status) => {
        const badges = {
            'ATIVA': { bg: 'bg-green-100', text: 'text-green-800', icon: CheckCircle },
            'BLOQUEADA': { bg: 'bg-red-100', text: 'text-red-800', icon: XCircle },
            'TRIAL': { bg: 'bg-blue-100', text: 'text-blue-800', icon: Clock },
            'EXPIRADA': { bg: 'bg-gray-100', text: 'text-gray-800', icon: AlertCircle }
        };

        const badge = badges[status] || badges['EXPIRADA'];
        const Icon = badge.icon;

        return (
            <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${badge.bg} ${badge.text}`}>
                <Icon className="w-3 h-3" />
                {status}
            </span>
        );
    };

    const getTipoBadge = (tipo) => {
        const tipos = {
            'TRIAL': { bg: 'bg-blue-50', text: 'text-blue-700' },
            'MANUAL': { bg: 'bg-purple-50', text: 'text-purple-700' },
            'AUTOMATICA': { bg: 'bg-gray-50', text: 'text-gray-700' }
        };

        const tipoConfig = tipos[tipo] || tipos['AUTOMATICA'];

        return (
            <span className={`px-2 py-1 rounded text-xs font-medium ${tipoConfig.bg} ${tipoConfig.text}`}>
                {tipo}
            </span>
        );
    };

    // Verificar se é admin global (baseado na coluna role)
    const isGlobalAdmin = user && ['global_admin', 'super_admin', 'admin'].includes(user.role);
    
    if (!isGlobalAdmin) {
        return null;
    }

    return (
        <div className="min-h-screen bg-gray-50 p-6">
            <PageTitle title="Gerenciar Assinaturas" />

            {alert.message && (
                <Alert 
                    type={alert.type} 
                    message={alert.message}
                    onClose={() => setAlert({ type: '', message: '' })}
                    className="mb-4"
                />
            )}

            {/* Estatísticas */}
            {estatisticas && (
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
                    <div className="bg-white rounded-lg shadow p-4">
                        <div className="flex items-center justify-between">
                            <div>
                                <p className="text-sm text-gray-600">Total Organizações</p>
                                <p className="text-2xl font-bold">{estatisticas.estatisticas?.total_organizacoes || 0}</p>
                            </div>
                            <Building2 className="w-8 h-8 text-blue-500" />
                        </div>
                    </div>
                    <div className="bg-white rounded-lg shadow p-4">
                        <div className="flex items-center justify-between">
                            <div>
                                <p className="text-sm text-gray-600">Ativas</p>
                                <p className="text-2xl font-bold text-green-600">{estatisticas.estatisticas?.organizacoes_ativas || 0}</p>
                            </div>
                            <CheckCircle className="w-8 h-8 text-green-500" />
                        </div>
                    </div>
                    <div className="bg-white rounded-lg shadow p-4">
                        <div className="flex items-center justify-between">
                            <div>
                                <p className="text-sm text-gray-600">Trials</p>
                                <p className="text-2xl font-bold text-blue-600">{estatisticas.estatisticas?.organizacoes_trial || 0}</p>
                            </div>
                            <Clock className="w-8 h-8 text-blue-500" />
                        </div>
                    </div>
                    <div className="bg-white rounded-lg shadow p-4">
                        <div className="flex items-center justify-between">
                            <div>
                                <p className="text-sm text-gray-600">Bloqueadas</p>
                                <p className="text-2xl font-bold text-red-600">{estatisticas.estatisticas?.organizacoes_bloqueadas || 0}</p>
                            </div>
                            <XCircle className="w-8 h-8 text-red-500" />
                        </div>
                    </div>
                </div>
            )}

            {/* Filtros */}
            <div className="bg-white rounded-lg shadow p-4 mb-6">
                <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Buscar</label>
                        <div className="relative">
                            <Search className="absolute left-2 top-2.5 w-4 h-4 text-gray-400" />
                            <input
                                type="text"
                                placeholder="Nome ou CNPJ..."
                                value={filters.search}
                                onChange={(e) => handleFilterChange('search', e.target.value)}
                                className="pl-8 w-full rounded-md border-gray-300"
                            />
                        </div>
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Status Assinatura</label>
                        <select
                            value={filters.status_assinatura}
                            onChange={(e) => handleFilterChange('status_assinatura', e.target.value)}
                            className="w-full rounded-md border-gray-300"
                        >
                            <option value="">Todos</option>
                            <option value="ATIVA">Ativa</option>
                            <option value="BLOQUEADA">Bloqueada</option>
                            <option value="TRIAL">Trial</option>
                            <option value="EXPIRADA">Expirada</option>
                        </select>
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Tipo Assinatura</label>
                        <select
                            value={filters.tipo_assinatura}
                            onChange={(e) => handleFilterChange('tipo_assinatura', e.target.value)}
                            className="w-full rounded-md border-gray-300"
                        >
                            <option value="">Todos</option>
                            <option value="TRIAL">Trial</option>
                            <option value="MANUAL">Manual</option>
                            <option value="AUTOMATICA">Automática</option>
                        </select>
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
                        <select
                            value={filters.status}
                            onChange={(e) => handleFilterChange('status', e.target.value)}
                            className="w-full rounded-md border-gray-300"
                        >
                            <option value="">Todos</option>
                            <option value="ATIVO">Ativo</option>
                            <option value="BLOQUEADO">Bloqueado</option>
                            <option value="CANCELADO">Cancelado</option>
                        </select>
                    </div>
                    <div className="flex items-end">
                        <PrimaryButton
                            onClick={carregarDados}
                            className="w-full"
                        >
                            <RefreshCw className="w-4 h-4 mr-2" />
                            Atualizar
                        </PrimaryButton>
                    </div>
                </div>
            </div>

            {/* Lista de Organizações */}
            <div className="bg-white rounded-lg shadow overflow-hidden">
                {loading ? (
                    <LoadingIndicator />
                ) : (
                    <>
                        <div className="overflow-x-auto">
                            <table className="min-w-full divide-y divide-gray-200">
                                <thead className="bg-gray-50">
                                    <tr>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            Organização
                                        </th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            Plano
                                        </th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            Status
                                        </th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            Tipo
                                        </th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            Expiração
                                        </th>
                                        <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            Ações
                                        </th>
                                    </tr>
                                </thead>
                                <tbody className="bg-white divide-y divide-gray-200">
                                    {organizacoes.length === 0 ? (
                                        <tr>
                                            <td colSpan="6" className="px-6 py-4 text-center text-gray-500">
                                                Nenhuma organização encontrada
                                            </td>
                                        </tr>
                                    ) : (
                                        organizacoes.map((org) => (
                                            <tr key={org.organizacao_id} className="hover:bg-gray-50">
                                                <td className="px-6 py-4 whitespace-nowrap">
                                                    <div>
                                                        <div className="text-sm font-medium text-gray-900">
                                                            {org.organizacao_nome}
                                                        </div>
                                                        <div className="text-sm text-gray-500">
                                                            {org.organizacao_cnpj || 'Sem CNPJ'}
                                                        </div>
                                                    </div>
                                                </td>
                                                <td className="px-6 py-4 whitespace-nowrap">
                                                    <div className="text-sm text-gray-900">
                                                        {org.plano_nome || 'N/A'}
                                                    </div>
                                                    {org.plano_valor && (
                                                        <div className="text-sm text-gray-500">
                                                            R$ {parseFloat(org.plano_valor).toFixed(2)}
                                                        </div>
                                                    )}
                                                </td>
                                                <td className="px-6 py-4 whitespace-nowrap">
                                                    {getStatusBadge(org.status_assinatura)}
                                                </td>
                                                <td className="px-6 py-4 whitespace-nowrap">
                                                    {org.tipo_assinatura && getTipoBadge(org.tipo_assinatura)}
                                                </td>
                                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                                    {org.data_expiracao 
                                                        ? new Date(org.data_expiracao).toLocaleDateString('pt-BR')
                                                        : 'N/A'
                                                    }
                                                </td>
                                                <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                                                    <button
                                                        onClick={() => handleAbrirDetalhes(org.organizacao_id)}
                                                        className="text-blue-600 hover:text-blue-900 mr-4"
                                                    >
                                                        <Edit className="w-4 h-4 inline" />
                                                    </button>
                                                </td>
                                            </tr>
                                        ))
                                    )}
                                </tbody>
                            </table>
                        </div>

                        {/* Paginação */}
                        {pagination.totalPages > 1 && (
                            <div className="bg-gray-50 px-4 py-3 flex items-center justify-between border-t border-gray-200">
                                <div className="text-sm text-gray-700">
                                    Mostrando {((pagination.page - 1) * pagination.limit) + 1} a {Math.min(pagination.page * pagination.limit, pagination.total)} de {pagination.total}
                                </div>
                                <div className="flex gap-2">
                                    <button
                                        onClick={() => setPagination(prev => ({ ...prev, page: prev.page - 1 }))}
                                        disabled={pagination.page === 1}
                                        className="px-3 py-1 border rounded disabled:opacity-50"
                                    >
                                        Anterior
                                    </button>
                                    <button
                                        onClick={() => setPagination(prev => ({ ...prev, page: prev.page + 1 }))}
                                        disabled={pagination.page === pagination.totalPages}
                                        className="px-3 py-1 border rounded disabled:opacity-50"
                                    >
                                        Próxima
                                    </button>
                                </div>
                            </div>
                        )}
                    </>
                )}
            </div>

            {/* Modal de Detalhes */}
            {showDetalhesModal && selectedOrganizacao && (
                <Modal
                    isOpen={showDetalhesModal}
                    onClose={() => {
                        setShowDetalhesModal(false);
                        setSelectedOrganizacao(null);
                    }}
                    title={`Detalhes - ${selectedOrganizacao.organizacao.nome}`}
                    size="large"
                >
                    <div className="space-y-4">
                        <div>
                            <h3 className="font-semibold mb-2">Informações da Organização</h3>
                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <p className="text-sm text-gray-600">Nome</p>
                                    <p className="font-medium">{selectedOrganizacao.organizacao.nome}</p>
                                </div>
                                <div>
                                    <p className="text-sm text-gray-600">CNPJ</p>
                                    <p className="font-medium">{selectedOrganizacao.organizacao.cnpj || 'N/A'}</p>
                                </div>
                                <div>
                                    <p className="text-sm text-gray-600">Status</p>
                                    {getStatusBadge(selectedOrganizacao.organizacao.status_assinatura)}
                                </div>
                            </div>
                        </div>

                        <div>
                            <h3 className="font-semibold mb-2">Assinaturas</h3>
                            {selectedOrganizacao.assinaturas && selectedOrganizacao.assinaturas.length > 0 ? (
                                <div className="space-y-2">
                                    {selectedOrganizacao.assinaturas.map((assinatura) => (
                                        <div key={assinatura.id} className="border rounded p-3">
                                            <div className="flex justify-between items-start">
                                                <div>
                                                    <p className="font-medium">{assinatura.plano_nome}</p>
                                                    <p className="text-sm text-gray-600">
                                                        {assinatura.status} • {getTipoBadge(assinatura.tipo_assinatura)}
                                                    </p>
                                                    {assinatura.data_expiracao && (
                                                        <p className="text-sm text-gray-500">
                                                            Expira em: {new Date(assinatura.data_expiracao).toLocaleDateString('pt-BR')}
                                                        </p>
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            ) : (
                                <p className="text-gray-500">Nenhuma assinatura encontrada</p>
                            )}
                        </div>

                        <div className="flex gap-2 pt-4 border-t">
                            <PrimaryButton
                                onClick={() => {
                                    setShowPlanoModal(true);
                                    setShowDetalhesModal(false);
                                }}
                                className="flex-1"
                            >
                                <Edit className="w-4 h-4 mr-2" />
                                Atualizar Plano
                            </PrimaryButton>
                            {selectedOrganizacao.organizacao.status_assinatura === 'BLOQUEADA' ? (
                                <PrimaryButton
                                    onClick={handleDesbloquear}
                                    disabled={actionLoading}
                                    className="flex-1 bg-green-600 hover:bg-green-700"
                                >
                                    <Unlock className="w-4 h-4 mr-2" />
                                    Desbloquear
                                </PrimaryButton>
                            ) : (
                                <PrimaryButton
                                    onClick={() => {
                                        setShowBloquearModal(true);
                                        setShowDetalhesModal(false);
                                    }}
                                    className="flex-1 bg-red-600 hover:bg-red-700"
                                >
                                    <Lock className="w-4 h-4 mr-2" />
                                    Bloquear
                                </PrimaryButton>
                            )}
                            <PrimaryButton
                                onClick={() => {
                                    setShowRenovarModal(true);
                                    setShowDetalhesModal(false);
                                }}
                                className="flex-1 bg-blue-600 hover:bg-blue-700"
                            >
                                <RefreshCw className="w-4 h-4 mr-2" />
                                Renovar
                            </PrimaryButton>
                        </div>
                    </div>
                </Modal>
            )}

            {/* Modal Atualizar Plano */}
            <Modal
                isOpen={showPlanoModal}
                onClose={() => {
                    setShowPlanoModal(false);
                    setFormData({ plano_id: '', observacoes: '', dias_adicionais: 365 });
                }}
                title="Atualizar Plano"
            >
                <div className="space-y-4">
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Plano</label>
                        <select
                            value={formData.plano_id}
                            onChange={(e) => setFormData(prev => ({ ...prev, plano_id: e.target.value }))}
                            className="w-full rounded-md border-gray-300"
                        >
                            <option value="">Selecione um plano</option>
                            {planos.map(plano => (
                                <option key={plano.id} value={plano.id}>
                                    {plano.nome} - R$ {parseFloat(plano.valor).toFixed(2)}/{plano.frequencia}
                                </option>
                            ))}
                        </select>
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Observações</label>
                        <textarea
                            value={formData.observacoes}
                            onChange={(e) => setFormData(prev => ({ ...prev, observacoes: e.target.value }))}
                            rows="3"
                            className="w-full rounded-md border-gray-300"
                            placeholder="Observações sobre a alteração..."
                        />
                    </div>
                    <div className="flex gap-2 pt-4">
                        <PrimaryButton
                            onClick={handleAtualizarPlano}
                            disabled={actionLoading || !formData.plano_id}
                            className="flex-1"
                        >
                            {actionLoading ? 'Salvando...' : 'Salvar'}
                        </PrimaryButton>
                        <button
                            onClick={() => {
                                setShowPlanoModal(false);
                                setFormData({ plano_id: '', observacoes: '', dias_adicionais: 365 });
                            }}
                            className="px-4 py-2 border rounded hover:bg-gray-50"
                        >
                            Cancelar
                        </button>
                    </div>
                </div>
            </Modal>

            {/* Modal Bloquear */}
            <Modal
                isOpen={showBloquearModal}
                onClose={() => {
                    setShowBloquearModal(false);
                    setFormData({ plano_id: '', observacoes: '', dias_adicionais: 365 });
                }}
                title="Bloquear Organização"
            >
                <div className="space-y-4">
                    <Alert type="warning" message="Esta ação bloqueará o acesso da organização ao sistema." />
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Motivo/Observações</label>
                        <textarea
                            value={formData.observacoes}
                            onChange={(e) => setFormData(prev => ({ ...prev, observacoes: e.target.value }))}
                            rows="3"
                            className="w-full rounded-md border-gray-300"
                            placeholder="Informe o motivo do bloqueio..."
                        />
                    </div>
                    <div className="flex gap-2 pt-4">
                        <PrimaryButton
                            onClick={handleBloquear}
                            disabled={actionLoading}
                            className="flex-1 bg-red-600 hover:bg-red-700"
                        >
                            {actionLoading ? 'Bloqueando...' : 'Confirmar Bloqueio'}
                        </PrimaryButton>
                        <button
                            onClick={() => {
                                setShowBloquearModal(false);
                                setFormData({ plano_id: '', observacoes: '', dias_adicionais: 365 });
                            }}
                            className="px-4 py-2 border rounded hover:bg-gray-50"
                        >
                            Cancelar
                        </button>
                    </div>
                </div>
            </Modal>

            {/* Modal Renovar */}
            <Modal
                isOpen={showRenovarModal}
                onClose={() => {
                    setShowRenovarModal(false);
                    setFormData({ plano_id: '', observacoes: '', dias_adicionais: 365 });
                }}
                title="Renovar Assinatura"
            >
                <div className="space-y-4">
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Dias Adicionais</label>
                        <input
                            type="number"
                            value={formData.dias_adicionais}
                            onChange={(e) => setFormData(prev => ({ ...prev, dias_adicionais: e.target.value }))}
                            min="1"
                            className="w-full rounded-md border-gray-300"
                        />
                        <p className="text-sm text-gray-500 mt-1">Padrão: 365 dias (1 ano)</p>
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Observações</label>
                        <textarea
                            value={formData.observacoes}
                            onChange={(e) => setFormData(prev => ({ ...prev, observacoes: e.target.value }))}
                            rows="3"
                            className="w-full rounded-md border-gray-300"
                            placeholder="Observações sobre a renovação..."
                        />
                    </div>
                    <div className="flex gap-2 pt-4">
                        <PrimaryButton
                            onClick={handleRenovar}
                            disabled={actionLoading}
                            className="flex-1"
                        >
                            {actionLoading ? 'Renovando...' : 'Confirmar Renovação'}
                        </PrimaryButton>
                        <button
                            onClick={() => {
                                setShowRenovarModal(false);
                                setFormData({ plano_id: '', observacoes: '', dias_adicionais: 365 });
                            }}
                            className="px-4 py-2 border rounded hover:bg-gray-50"
                        >
                            Cancelar
                        </button>
                    </div>
                </div>
            </Modal>
        </div>
    );
};

export default AdminAssinaturasPage;
